/**
 * Emergency Utilities
 * Helper functions, validators, and constants for emergency functionality
 */

// Kenya Crisis Hotlines
export const CRISIS_HOTLINES = {
  befrienders: {
    name: 'Befrienders Kenya',
    number: '0800 720 648',
    available: '24/7',
    description: 'Suicide prevention and emotional support',
  },
  gvrc: {
    name: 'Gender Violence Recovery Centre',
    number: '116',
    available: '24/7',
    description: 'Gender-based violence support and counseling',
  },
  gbvHotline: {
    name: 'GBV Hotline',
    number: '1195',
    available: '24/7',
    description: 'Gender-based violence emergency hotline',
  },
  nacada: {
    name: 'NACADA',
    number: '0800 220 002',
    available: 'Office hours',
    description: 'Substance abuse counseling and support',
  },
  childline: {
    name: 'Childline Kenya',
    number: '1199',
    available: '24/7',
    description: 'Child protection and support services',
  },
  emergency: {
    name: 'Emergency Services',
    number: '999 or 112',
    available: '24/7',
    description: 'Police, ambulance, and fire emergency services',
  },
};

// Crisis Types
export const CRISIS_TYPES = {
  SUICIDE_IDEATION: 'suicide-ideation',
  SELF_HARM: 'self-harm',
  PANIC_ATTACK: 'panic-attack',
  SEVERE_ANXIETY: 'severe-anxiety',
  TRAUMA_RESPONSE: 'trauma-response',
  DOMESTIC_VIOLENCE: 'domestic-violence',
  SEXUAL_ASSAULT: 'sexual-assault',
  SUBSTANCE_ABUSE: 'substance-abuse-crisis',
  ACUTE_DEPRESSION: 'acute-depression',
  PSYCHOTIC_EPISODE: 'psychotic-episode',
  OTHER: 'other-emergency',
};

// Session Types
export const SESSION_TYPES = {
  PHYSICAL: 'physical',
  VIRTUAL: 'virtual',
};

// Appointment Status
export const APPOINTMENT_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  NO_SHOW: 'no-show',
};

/**
 * Get recommended hotlines based on crisis type
 * @param {string} crisisType - Type of crisis
 * @returns {Array} Array of recommended hotlines
 */
export const getRecommendedHotlines = (crisisType) => {
  const hotlineMap = {
    [CRISIS_TYPES.SUICIDE_IDEATION]: [
      CRISIS_HOTLINES.befrienders,
      CRISIS_HOTLINES.emergency,
    ],
    [CRISIS_TYPES.SELF_HARM]: [CRISIS_HOTLINES.befrienders],
    [CRISIS_TYPES.DOMESTIC_VIOLENCE]: [
      CRISIS_HOTLINES.gbvHotline,
      CRISIS_HOTLINES.gvrc,
      CRISIS_HOTLINES.emergency,
    ],
    [CRISIS_TYPES.SEXUAL_ASSAULT]: [
      CRISIS_HOTLINES.gvrc,
      CRISIS_HOTLINES.gbvHotline,
      CRISIS_HOTLINES.emergency,
    ],
    [CRISIS_TYPES.SUBSTANCE_ABUSE]: [CRISIS_HOTLINES.nacada],
    [CRISIS_TYPES.PSYCHOTIC_EPISODE]: [CRISIS_HOTLINES.emergency],
  };

  return hotlineMap[crisisType] || [
    CRISIS_HOTLINES.befrienders,
    CRISIS_HOTLINES.emergency,
  ];
};

/**
 * Validate emergency appointment data
 * @param {Object} data - Appointment data to validate
 * @returns {Object} { isValid: boolean, errors: Object }
 */
export const validateEmergencyAppointment = (data) => {
  const errors = {};

  // Crisis type validation
  if (!data.crisisType) {
    errors.crisisType = 'Crisis type is required';
  }

  // Session type validation
  if (!data.sessionType) {
    errors.sessionType = 'Session type is required';
  }

  // For guest appointments, validate contact information
  if (!data.userId) {
    if (!data.guestName || data.guestName.trim().length < 2) {
      errors.guestName = 'Name must be at least 2 characters';
    }

    if (!data.guestEmail) {
      errors.guestEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.guestEmail)) {
      errors.guestEmail = 'Invalid email format';
    }

    if (!data.guestPhone) {
      errors.guestPhone = 'Phone number is required';
    } else if (!/^(\+?254|0)[17]\d{8}$/.test(data.guestPhone.replace(/\s/g, ''))) {
      errors.guestPhone = 'Invalid Kenyan phone number';
    }
  }

  // Emergency contact validation
  if (data.emergencyContactName && data.emergencyContactName.trim().length < 2) {
    errors.emergencyContactName = 'Emergency contact name must be at least 2 characters';
  }

  if (data.emergencyContactPhone &&
      !/^(\+?254|0)[17]\d{8}$/.test(data.emergencyContactPhone.replace(/\s/g, ''))) {
    errors.emergencyContactPhone = 'Invalid Kenyan phone number';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

/**
 * Format phone number to Kenyan standard
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
  const cleaned = phone.replace(/\D/g, '');

  if (cleaned.startsWith('254')) {
    return `+${cleaned}`;
  }

  if (cleaned.startsWith('0')) {
    return `+254${cleaned.substring(1)}`;
  }

  return `+254${cleaned}`;
};

/**
 * Get crisis severity level
 * @param {string} crisisType - Type of crisis
 * @returns {string} Severity level (low, medium, high, critical)
 */
export const getCrisisSeverity = (crisisType) => {
  const criticalCrises = [
    CRISIS_TYPES.SUICIDE_IDEATION,
    CRISIS_TYPES.PSYCHOTIC_EPISODE,
  ];

  const highCrises = [
    CRISIS_TYPES.SELF_HARM,
    CRISIS_TYPES.DOMESTIC_VIOLENCE,
    CRISIS_TYPES.SEXUAL_ASSAULT,
  ];

  const mediumCrises = [
    CRISIS_TYPES.PANIC_ATTACK,
    CRISIS_TYPES.SEVERE_ANXIETY,
    CRISIS_TYPES.TRAUMA_RESPONSE,
  ];

  if (criticalCrises.includes(crisisType)) return 'critical';
  if (highCrises.includes(crisisType)) return 'high';
  if (mediumCrises.includes(crisisType)) return 'medium';
  return 'low';
};

/**
 * Get color scheme based on severity
 * @param {string} severity - Severity level
 * @returns {Object} Color configuration
 */
export const getSeverityColors = (severity) => {
  const colorMap = {
    critical: {
      bg: 'bg-red-50',
      border: 'border-red-500',
      text: 'text-red-900',
      badge: 'bg-red-600',
    },
    high: {
      bg: 'bg-orange-50',
      border: 'border-orange-500',
      text: 'text-orange-900',
      badge: 'bg-orange-600',
    },
    medium: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-500',
      text: 'text-yellow-900',
      badge: 'bg-yellow-600',
    },
    low: {
      bg: 'bg-green-50',
      border: 'border-green-500',
      text: 'text-green-900',
      badge: 'bg-green-600',
    },
  };

  return colorMap[severity] || colorMap.low;
};

/**
 * Get status badge color
 * @param {string} status - Appointment status
 * @returns {string} Tailwind class for badge color
 */
export const getStatusBadgeColor = (status) => {
  const colorMap = {
    [APPOINTMENT_STATUS.PENDING]: 'bg-yellow-100 text-yellow-800',
    [APPOINTMENT_STATUS.CONFIRMED]: 'bg-blue-100 text-blue-800',
    [APPOINTMENT_STATUS.IN_PROGRESS]: 'bg-purple-100 text-purple-800',
    [APPOINTMENT_STATUS.COMPLETED]: 'bg-green-100 text-green-800',
    [APPOINTMENT_STATUS.CANCELLED]: 'bg-gray-100 text-gray-800',
    [APPOINTMENT_STATUS.NO_SHOW]: 'bg-red-100 text-red-800',
  };

  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

/**
 * Format crisis type for display
 * @param {string} crisisType - Crisis type constant
 * @returns {string} Human-readable crisis type
 */
export const formatCrisisType = (crisisType) => {
  const typeMap = {
    [CRISIS_TYPES.SUICIDE_IDEATION]: 'Suicidal Thoughts',
    [CRISIS_TYPES.SELF_HARM]: 'Self-Harm',
    [CRISIS_TYPES.PANIC_ATTACK]: 'Panic Attack',
    [CRISIS_TYPES.SEVERE_ANXIETY]: 'Severe Anxiety',
    [CRISIS_TYPES.TRAUMA_RESPONSE]: 'Trauma Response',
    [CRISIS_TYPES.DOMESTIC_VIOLENCE]: 'Domestic Violence',
    [CRISIS_TYPES.SEXUAL_ASSAULT]: 'Sexual Assault',
    [CRISIS_TYPES.SUBSTANCE_ABUSE]: 'Substance Abuse Crisis',
    [CRISIS_TYPES.ACUTE_DEPRESSION]: 'Severe Depression',
    [CRISIS_TYPES.PSYCHOTIC_EPISODE]: 'Psychotic Episode',
    [CRISIS_TYPES.OTHER]: 'Other Emergency',
  };

  return typeMap[crisisType] || crisisType;
};

/**
 * Check if immediate action is required
 * @param {string} crisisType - Type of crisis
 * @returns {boolean} True if immediate action required
 */
export const requiresImmediateAction = (crisisType) => {
  const immediateCrises = [
    CRISIS_TYPES.SUICIDE_IDEATION,
    CRISIS_TYPES.PSYCHOTIC_EPISODE,
    CRISIS_TYPES.SELF_HARM,
  ];

  return immediateCrises.includes(crisisType);
};

/**
 * Generate emergency appointment confirmation message
 * @param {Object} appointment - Appointment object
 * @returns {string} Confirmation message
 */
export const generateConfirmationMessage = (appointment) => {
  const hotlines = getRecommendedHotlines(appointment.crisisType);
  const hotlineText = hotlines.map(h => `${h.name}: ${h.number}`).join(', ');

  return `Your emergency appointment has been scheduled. ${
    requiresImmediateAction(appointment.crisisType)
      ? 'This is a high-priority case. '
      : ''
  }If you need immediate help, please contact: ${hotlineText}`;
};

export default {
  CRISIS_HOTLINES,
  CRISIS_TYPES,
  SESSION_TYPES,
  APPOINTMENT_STATUS,
  getRecommendedHotlines,
  validateEmergencyAppointment,
  formatPhoneNumber,
  getCrisisSeverity,
  getSeverityColors,
  getStatusBadgeColor,
  formatCrisisType,
  requiresImmediateAction,
  generateConfirmationMessage,
};
